import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { useAsync } from "@/lib/useAsync";
import { useAuthUser } from "@/lib/auth";
import { fetchDashboard } from "@/lib/queries";
import { formatDate } from "@/lib/direction";

const SUPPORT_URL =
  import.meta.env.VITE_SUPPORT_URL ?? "https://wa.me/966533412600";

function isSubscriptionPath(pathname: string): boolean {
  return pathname === "/subscription" || pathname.startsWith("/subscription/");
}

function isPublicPath(pathname: string): boolean {
  return pathname === "/login" || pathname === "/admin/login";
}

function isAllowedPath(pathname: string): boolean {
  return isSubscriptionPath(pathname) || isPublicPath(pathname);
}

/**
 * Blocks the central portal when the centre's subscription has ended and shows
 * a non-dismissable dialog until the user renews or contacts support.
 *
 * The dashboard endpoint is the source of truth because an expired subscription
 * is deactivated by the backend and therefore no longer returned by
 * `/central/subscription` (null would be indistinguishable from "never had one").
 */
export function SubscriptionGuard({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAuthUser();
  const isCentral = user?.role === "central";
  const centralId = user?.central_id ?? user?.id;

  const [blockedAttempt, setBlockedAttempt] = useState(false);

  const { data } = useAsync(
    () => (isCentral ? fetchDashboard() : Promise.resolve(null)),
    [isCentral, centralId],
  );

  const ended =
    isCentral &&
    (data?.subscription_status === "ended" ||
      Boolean(data?.subscription_ended_at));
  const shouldBlock = ended && !isAllowedPath(pathname);

  const blocker = useBlocker(
    ({ nextLocation }) => ended && !isAllowedPath(nextLocation.pathname),
  );

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    setBlockedAttempt(true);
    blocker.reset();
  }, [blocker]);

  useEffect(() => {
    setBlockedAttempt(false);
  }, [pathname]);

  const showDialog = ended && (shouldBlock || blockedAttempt);

  useEffect(() => {
    if (!showDialog) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [showDialog]);

  const endedAt = data?.subscription_ended_at;
  const message = endedAt
    ? t("subscription.guardMessage", { date: formatDate(endedAt) })
    : t("subscription.guardMessageNoDate");

  return (
    <>
      {children}
      {showDialog &&
        createPortal(
          <div
            className="fixed inset-0 z-[2000] grid place-items-center bg-navy/60 px-4 py-8 backdrop-blur-sm"
            role="presentation"
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="subscription-ended-title"
              aria-describedby="subscription-ended-message"
              className="modal-surface relative w-full max-w-[460px] p-6"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose to-amber" />
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-rose/10 text-rose">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5" />
                  <path d="M12 16h.01" />
                </svg>
              </div>
              <h2
                id="subscription-ended-title"
                className="mt-4 text-lg font-extrabold text-ink-1"
              >
                {t("subscription.guardTitle")}
              </h2>
              <p
                id="subscription-ended-message"
                className="mt-2 text-[13.5px] leading-relaxed text-ink-2"
              >
                {message}
              </p>
              <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                <Button
                  autoFocus
                  className="sm:flex-1"
                  onClick={() => navigate("/subscription/renew")}
                >
                  {t("subscription.guardRenew")}
                </Button>
                <Button
                  variant="ghost"
                  className="sm:flex-1"
                  onClick={() =>
                    window.open(SUPPORT_URL, "_blank", "noopener,noreferrer")
                  }
                >
                  {t("subscription.guardContactSupport")}
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
